import React, { useEffect, useState, ComponentType, SetStateAction } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

export interface TextInputProps {
	id: string;
	name: string;
	placeholder: string;
	value?: string;
	label: string;
	disabled?: boolean;
	testId?: string;
	validation?: (arg0: string) => [boolean, string];
	Icon?: ComponentType;
	onChangeHandler?: React.Dispatch<SetStateAction<string>>;
	className?: string;
	autocomplete?: string | undefined;
}

const containerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	position: 'relative',

	'.valid-inp': {
		'&:focus': {
			borderColor: '#4c9aff',
		},
	},
	'.invalid-inp': {
		'&:focus': {
			borderColor: '#de350b',
		},
	},
});

const labelStyles = css({
	color: '#253858',
	fontSize: '0.75rem',
	lineHeight: '1.1',
	marginBlockEnd: token('space.050'),
	textAlign: 'left',
});

const inputContainerStyles = css({
	position: 'relative',
});

const inputStyles = css({
	boxSizing: 'border-box',
	backgroundColor: '#fafbfc',
	borderWidth: '2px',
	borderStyle: 'solid',
	borderColor: '#dfe1e6',
	borderRadius: '4px',
	flexShrink: 0,
	height: '2.65rem',
	paddingInlineStart: '8px',
	width: '100%',
	fontSize: '1rem',
	lineHeight: '1.2',
	textAlign: 'left',
	color: '#091e42',

	'&::placeholder': {
		color: '#a5adba',
	},

	'&:hover': {
		borderColor: '#dfe1e6',
		background: '#ebecf0',
	},

	'&:disabled': {
		borderColor: '#dfe1e6',
		background: '#f4f5f7',
		color: '#a5adba',
	},

	'&:focus': {
		outline: '1px solid transparent',
	},
});

const errorStyles = css({
	color: '#de350b',
	fontSize: '0.75rem',
	fontStyle: 'normal',
	fontWeight: '400',
	lineHeight: '1.2',
	margin: 0,
	marginBlockStart: '4px',
});

const iconStyles = css({
	position: 'absolute',
	margin: 'auto',
	insetBlockStart: 0,
	insetBlockEnd: 0,
	insetInlineEnd: '10px',
	width: '24px',
	height: '24px',
	pointerEvents: 'none',
});

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>((props, ref) => {
	const {
		name,
		value,
		id,
		testId,
		label,
		placeholder,
		disabled,
		validation,
		Icon,
		onChangeHandler,
		className,
		autocomplete,
	} = props;

	const [text, setText] = useState('');
	const [isValid, setValid] = useState(true);
	const [error, setError] = useState('');

	useEffect(() => {
		if (text && validation) {
			const { 0: valid, 1: error } = validation(text);
			if (valid) {
				setError('');
			} else {
				setError(error);
			}
			setValid(valid);
		}

		if (!text) {
			setError('');
		}
	}, [text]);

	return (
		<div className={className} css={containerStyles}>
			<label css={labelStyles} htmlFor={name}>
				{label}
			</label>
			<div className="input-container" css={inputContainerStyles}>
				<input
					className={isValid ? 'valid-inp' : 'invalid-inp'}
					onChange={(e) => {
						setText(e.target.value);
						onChangeHandler && onChangeHandler(e.target.value);
					}}
					css={inputStyles}
					type="text"
					ref={ref}
					name={name}
					id={id}
					data-testid={testId ? testId : id}
					value={value}
					placeholder={placeholder}
					disabled={disabled || false}
					aria-invalid={!!error}
					aria-errormessage={!!error ? `err${id}` : ''}
					autoComplete={autocomplete}
				/>
				{Icon && (
					<div css={iconStyles}>
						<Icon />
					</div>
				)}
			</div>
			{!!error && (
				<label id={`err${id}`} css={errorStyles}>
					{error}
				</label>
			)}
		</div>
	);
});

TextInput.displayName = 'TextInput';

export { TextInput };
